import { useEffect, useRef, useState } from 'react';
import { CheckIcon } from 'lucide-react';
import type { InstellingGroep } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '../../lib/apiClient';
import { useBewaarInstellingen, useInstellingen } from '../beheer/beheer.api';

/**
 * De getalinstellingen van één groep, met één opslaanknop.
 *
 * Eén component voor alle groepen: de knoppen zijn hetzelfde formulier en
 * alleen de kop en de selectie verschillen. Het paneel stuurt bij opslaan
 * alleen zijn eigen sleutels mee, zodat het niet de waarde van een ander paneel
 * kan terugzetten.
 */
function GetallenPaneel({ groep, titel }: { groep: InstellingGroep; titel: string }) {
  const { data } = useInstellingen();
  const bewaar = useBewaarInstellingen();
  const [waarden, setWaarden] = useState<Record<string, number>>({});
  const [melding, setMelding] = useState('');
  const [fout, setFout] = useState('');
  const geladen = useRef<string>('');

  const eigen = (data?.definities ?? []).filter((def) => def.groep === groep);

  useEffect(() => {
    if (!data) return;
    const mijn: Record<string, number> = {};
    for (const def of data.definities) {
      if (def.groep === groep) mijn[def.sleutel] = data.waarden[def.sleutel] ?? def.standaard;
    }
    const vers = JSON.stringify(mijn);
    if (geladen.current !== vers) {
      geladen.current = vers;
      setWaarden(mijn);
    }
  }, [data, groep]);

  const gewijzigd = Boolean(data) && JSON.stringify(waarden) !== geladen.current;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{titel}</CardTitle>
        </CardHeader>
        <CardContent>
          <FieldGroup>
            {eigen.map((def) => (
              <Field key={def.sleutel}>
                <FieldLabel htmlFor={`instelling-${def.sleutel}`}>{def.naam}</FieldLabel>
                {/* De eenheid staat ín het veld en niet ernaast: "dagen" hoort
                    bij het getal en niet bij de rij. */}
                <InputGroup className="w-48">
                  <InputGroupInput
                    id={`instelling-${def.sleutel}`}
                    max={def.max}
                    min={def.min}
                    onChange={(e) =>
                      setWaarden((w) => ({ ...w, [def.sleutel]: Number(e.target.value) }))
                    }
                    type="number"
                    value={waarden[def.sleutel] ?? def.standaard}
                  />
                  <InputGroupAddon align="inline-end">{def.eenheid}</InputGroupAddon>
                </InputGroup>
                <FieldDescription>{def.toelichting}</FieldDescription>
              </Field>
            ))}
          </FieldGroup>
        </CardContent>
        <CardFooter className="justify-end">
          <Button
            disabled={!gewijzigd || bewaar.isPending}
            onClick={async () => {
              setFout('');
              setMelding('');
              try {
                await bewaar.mutateAsync(waarden);
                geladen.current = JSON.stringify(waarden);
                setMelding('Opgeslagen.');
              } catch (err) {
                setFout(err instanceof ApiError ? err.message : String(err));
              }
            }}
          >
            {bewaar.isPending && <Spinner data-icon="inline-start" />}
            {bewaar.isPending ? 'Bezig…' : 'Opslaan'}
          </Button>
        </CardFooter>
      </Card>

      {melding && !gewijzigd && (
        <Alert>
          <CheckIcon />
          <AlertTitle>{melding}</AlertTitle>
        </Alert>
      )}
      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}
    </div>
  );
}

/** Bewaartermijnen: hoe lang iets blijft staan. */
export function BewarenPaneel() {
  return <GetallenPaneel groep="bewaren" titel="Bewaren" />;
}

/**
 * De drempel voor achterstallige begeleiding.
 *
 * Die stond op het begeleidingsscherm zelf, op de plek waar hij effect heeft.
 * Dat is teruggedraaid: het is iets wat je één keer instelt, en op een scherm
 * waar je mensen aan een buddy hangt is een instelling alleen ruis.
 */
export function BegeleidingPaneel() {
  return <GetallenPaneel groep="begeleiding" titel="Begeleiding" />;
}
